import React from 'react';

import { FIELD_CONFIG, formatWaDtiBps } from 'configs/columns';
import { CarveSpecification } from 'features/deals/DealStages/EventActionCards/CarveDetailsCardBody/types';
import { CarveSummary } from 'features/deals/DealStages/EventActionCards/CarveDiffTable/__generated__/fragments';
import { IndeterminateCheckbox } from 'ui-kit';
import {
  CarveDetailsValue,
  CarveDetailsLabel,
  CarveDetailsContainer,
  CarveDetailsRow,
  CarveName,
} from 'ui-kit';

const carveDetailsTableRows = [
  {
    label: 'O BAL',
    value: (carve: CarveSummary) => carve.original_balance_cents,
    display: FIELD_CONFIG.original_balance_cents.display,
  },
  {
    label: 'UPB',
    value: (carve: CarveSummary) => carve.current_balance_cents,
    display: FIELD_CONFIG.current_balance_cents.display,
  },
  {
    label: 'UPB/O BAL',
    value: (carve: CarveSummary) => carve.current_balance_cents / carve.original_balance_cents,
    display: (value: number) => `${(value * 100).toFixed(2)}%`,
  },
  {
    label: '# LOANS',
    value: (carve: CarveSummary) => carve.loan_count,
    display: (value: number) => value.toLocaleString(),
  },
  {
    label: 'WALA',
    value: (carve: CarveSummary) => carve.wa_age_months,
    display: FIELD_CONFIG.age_months.display,
  },
  {
    label: 'WAM',
    value: (carve: CarveSummary) => carve.wa_remaining_loan_terms_months,
    display: FIELD_CONFIG.remaining_loan_terms.display,
  },
  {
    label: 'GWAC',
    value: (carve: CarveSummary) => carve.wa_coupon_bps,
    display: FIELD_CONFIG.interest_rate_bps.display,
  },
  {
    label: 'FICO',
    value: (carve: CarveSummary) => carve.wa_borrower_credit_score,
    display: FIELD_CONFIG.borrower_credit_score.display,
  },
  {
    label: 'LTV',
    value: (carve: CarveSummary) => carve.wa_ltv_bps,
    display: FIELD_CONFIG.ltv_bps.display,
  },
  {
    label: 'DTI',
    value: (carve: CarveSummary) => carve.wa_dti_bps,
    display: formatWaDtiBps,
  },
  // {
  //   label: 'DQ',
  //   value: (carve: CarveSummary) => '-', // You can adjust this as needed
  //   display: (value: string) => value,
  // },
];

const CarveDetailsTable = ({ carveSummary }: { carveSummary: CarveSummary }) => {
  return (
    <CarveDetailsContainer>
      {carveDetailsTableRows.map((rowConfig, index) => (
        <CarveDetailsRow key={index}>
          <CarveDetailsLabel>{rowConfig.label}</CarveDetailsLabel>
          <CarveDetailsValue>{rowConfig.display(rowConfig.value(carveSummary))}</CarveDetailsValue>
        </CarveDetailsRow>
      ))}
    </CarveDetailsContainer>
  );
};

export interface CarveCardProps {
  isSelected: boolean;
  carve: CarveSpecification;
  onCarveSelected: () => void;
}

const CarveCard: React.FC<CarveCardProps> = ({ carve, isSelected, onCarveSelected }) => {
  const handleCardClick = (event: React.MouseEvent) => {
    // Don't trigger selection if user is selecting text
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }

    // Don't trigger if clicking on the checkbox itself
    if ((event.target as HTMLElement).closest('input[type="checkbox"]')) {
      return;
    }

    onCarveSelected();
  };

  return (
    <>
      <div className="relative cursor-pointer select-text" onClick={handleCardClick}>
        <CarveName className="!text-left">
          <div className="flex items-center gap-2">
            <IndeterminateCheckbox
              checked={isSelected}
              onClick={(e) => {
                e.stopPropagation();
                onCarveSelected();
              }}
              onChange={(event) => {
                event.stopPropagation();
                if (event.target.checked) {
                  onCarveSelected();
                }
              }}
            />
            <span>
              {carve.name}
              {carve.isDraft ? ' (not saved)' : ''}
              {carve.isUpdating ? ' (updating...)' : ''}
              {carve.isDeleted ? ' (deleting...)' : ''}
            </span>
          </div>
        </CarveName>
        {isSelected && (
          <div className="pointer-events-none absolute -right-2 -top-2 rounded-full bg-pink-500 px-2 py-1 text-xs font-medium text-white shadow-lg">
            EDITING
          </div>
        )}
      </div>
      {carve.performanceSummary && (
        <div className="cursor-pointer select-text" onClick={handleCardClick}>
          <CarveDetailsTable carveSummary={carve.performanceSummary} />
        </div>
      )}
    </>
  );
};

export { CarveCard, CarveDetailsTable };
